<template>
  <div ref="hostEl" class="screen game" :style="{ '--pg-scale': gameScale }">
    <!-- SAFE FALLBACK: unknown / unsupported meta.id (§7). No game is started, no
         finished event, no fabricated result — just a clear status + safe exit. -->
    <template v-if="!supported">
      <div class="game__body game__fallback">
        <div class="game__fallback-emoji" aria-hidden="true">🎮</div>
        <h1 class="game__title">Dit spel is niet beschikbaar</h1>
        <p class="game__copy">
          We konden dit spel op dit moment niet openen. Ga terug en probeer een ander spel.
        </p>
      </div>
      <div class="screen__footer">
        <AppButton variant="light" @click="safeExit">Terug naar home</AppButton>
      </div>
    </template>

    <!-- Safe fallback for an async load / runtime failure (§4/§7) -->
    <template v-else-if="loadError">
      <div class="game__body game__fallback">
        <div class="game__fallback-emoji" aria-hidden="true">⚠️</div>
        <h1 class="game__title">Er ging iets mis met het spel</h1>
        <p class="game__copy">Het spel kon niet worden geladen. Probeer het opnieuw.</p>
      </div>
      <div class="screen__footer game__fallback-actions">
        <AppButton variant="light" @click="retryLoad">Opnieuw proberen</AppButton>
        <button class="game__textlink" type="button" @click="safeExit">Terug naar home</button>
      </div>
    </template>

    <!-- uitleg / play / score are the game's own Figma-matching screens, selected
         via the registry and switched by :phase. The shell owns the phase
         transitions + the quit-confirm; the score screen shows the CENTRAL stars
         (passed as :stars = store.lastStarsEarned), never the game's self-computed
         value. @start advances uitleg→play; @done (Verder) returns home. -->
    <!-- The game's uitleg screen rises up from the bottom on entry — the same
         slide-up that used to be the home "we beginnen zo…" reveal card. `appear`
         plays it once, on mount; later phase changes reuse this instance. -->
    <transition v-else appear name="rise">
      <component
        :is="gameComponent"
        :key="sessionKey"
        :meta="meta"
        :phase="phase"
        :paused="confirmQuit"
        :stars="store.lastStarsEarned"
        @start="startPlay"
        @finished="onFinished"
        @quit="onQuit"
        @done="done"
      />
    </transition>

    <!-- spel-verlaten popup (§5.2) — shell-owned confirm -->
    <transition name="fade">
      <div v-if="confirmQuit" class="game__overlay">
        <div class="game__modal">
          <h2>Spel verlaten?</h2>
          <p>Je speelbeurt wordt gebruikt en je ontvangt 0 sterren voor dit spel.</p>
          <div class="game__modal-actions">
            <button class="modal-btn modal-btn--ghost" type="button" @click="cancelQuit">
              Toch blijven
            </button>
            <button class="modal-btn modal-btn--primary" type="button" @click="quit">
              Verlaten
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, onErrorCaptured } from 'vue'
import { useRouter } from 'vue-router'
import { useGameStore } from '@/stores/game'
import AppButton from '@/components/ui/AppButton.vue'
import { createGameComponent, isSupportedGame } from '@/games/registry'
import { isValidFinishedPayload } from '@/games/contract'
import '@/games/gameHost.css'

const store = useGameStore()
const router = useRouter()

// Snapshot the reserved game (set by spin, before this mounts) ONCE. finishGame /
// quitGame clear server.activeGame, but the shell still owns the score + confirm
// screens for THIS session, so meta must stay stable for the life of the view — a
// new play session is always a fresh GameView mount.
const gameId = store.activeGame?.gameId ?? null
const meta = store.activeGameMeta
// A game is playable only when its meta exists AND it is explicitly registered.
const supported = !!meta && isSupportedGame(gameId)

const phase = ref('uitleg') // uitleg | play | score
const confirmQuit = ref(false)

// Per-play-session guards. `resolved` flips once a session is settled (finished
// or quit) so double / late events cannot be processed twice. `loadKey` gives a
// stable session identity that only changes on an explicit retry — a normal
// rerender or cancelling the quit-confirm never remounts the game.
const resolved = ref(false)
const loadKey = ref(0)
const loadError = ref(null)

const sessionKey = computed(() => `sess-${gameId ?? 'none'}-${loadKey.value}`)
const gameComponent = computed(() => {
  void loadKey.value // re-create on retry
  return supported ? createGameComponent(gameId) : null
})

// --- Game fit ---------------------------------------------------------------
// The four vendored games are fixed 390px canvases (see games/gameHost.css), so
// they are scaled to whatever column they land in rather than reflowed — flush
// left on a 430px phone and clipped on a 320px one otherwise. The scale has to be
// a plain NUMBER for `transform: scale()` and CSS cannot divide a length down to
// one, so it is measured here and handed down as `--pg-scale`.
const GAME_CANVAS_W = 390
const hostEl = ref(null)
const gameScale = ref(1)

function fitGame() {
  const el = hostEl.value
  if (!el) return
  const { width } = el.getBoundingClientRect()
  if (!width) return
  gameScale.value = width / GAME_CANVAS_W
}

let ro = null
onMounted(() => {
  // Nothing reserved → there is nothing to play (e.g. a direct /game visit).
  if (!store.activeGame) router.replace({ name: 'home' })
  fitGame()
  if (typeof ResizeObserver !== 'function') return
  ro = new ResizeObserver(fitGame)
  ro.observe(hostEl.value)
})
onBeforeUnmount(() => ro?.disconnect())

// Catch async chunk-load failures (and any runtime error) from the game subtree
// and show the safe load-error fallback with a retry, instead of a blank screen.
onErrorCaptured((err) => {
  console.error('[GameView] minigame failed to load/run:', err)
  loadError.value = err?.message || 'load_failed'
  return false
})

function startPlay() {
  phase.value = 'play'
}

// A minigame emits 'finished' at most once; the shell is still defensive against
// double / late / invalid events and never processes a result twice (§3).
async function onFinished(result) {
  if (resolved.value) return // already finished or quit this session
  if (!isValidFinishedPayload(result)) {
    console.warn('[GameView] ignoring invalid finished payload:', result)
    return
  }
  resolved.value = true
  confirmQuit.value = false // a real result supersedes a pending quit-confirm
  await store.finishGame({ gameId, ...result })
  phase.value = 'score'
}

// emit('quit') asks the shell to open its confirm — it does NOT leave/reset.
function onQuit() {
  if (resolved.value) return
  confirmQuit.value = true
}

function cancelQuit() {
  // Keep the same play session: the game stays mounted (never remounted here),
  // and a paused real-time game resumes because :paused goes back to false.
  confirmQuit.value = false
}

async function quit() {
  if (resolved.value) {
    confirmQuit.value = false
    return
  }
  resolved.value = true
  confirmQuit.value = false
  await store.quitGame({ gameId })
  router.replace({ name: 'home' })
}

// Safe exit for the unavailable / load-error fallbacks: release the reserved turn
// (so a reload does not re-trap on the same broken game) WITHOUT fabricating a
// result, then return home. quitGame awards 0 stars and clears the active game.
async function safeExit() {
  if (!resolved.value) {
    resolved.value = true
    if (gameId) {
      try {
        await store.quitGame({ gameId })
      } catch (e) {
        console.error('[GameView] safeExit quit failed:', e)
      }
    }
  }
  router.replace({ name: 'home' })
}

function retryLoad() {
  loadError.value = null
  loadKey.value += 1 // remount → re-import the game chunk
}

function done() {
  router.replace({ name: 'home' })
}
</script>

<style lang="scss" scoped>
.game {
  align-items: center;
  color: var(--c-on-brand);
  // clip the uitleg's slide-up entrance (the "rise" transition) so it never
  // produces a transient scrollbar while it animates in from below.
  overflow: hidden;
  // Figma 393:428 / 393:269 — linear gradient, dark red → brand red (not radial)
  background: linear-gradient(38.687deg, #820000 12.631%, #e60000 96.572%);
}
.game::before {
  // Figma: 2px white hairline across the very top (Rectangle 2287, 390×2)
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  z-index: 4;
  width: 100%;
  height: 2px;
  background: var(--c-white);
}

.game__close {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 75px;
  height: 60px;
  color: var(--c-ink);
  background: var(--c-white);
  border: none;
  border-radius: 0 0 23px 0;
}

.game__body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  gap: var(--sp-4);
  width: 100%;
  padding: 120px var(--sp-6) 0;
}
.game__art {
  font-size: 88px;
  line-height: 1;
}

.game__marquee {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1;
  pointer-events: none;
}
.game .screen__footer {
  padding: var(--sp-5) var(--sp-6);
  padding-bottom: max(50px, env(safe-area-inset-bottom));
}
.game__body--uitleg .game__art {
  // Figma 393:269: reserve the illustration footprint (y135–316) so the
  // title/tip/button land at their Figma positions despite the placeholder glyph.
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 176px;
}
.game__body--score {
  // Figma 393:428: no marquee. Grouped rhythm — art (top), "N Punten" (~y210),
  // stars+bar (~y266), then title/copy (~y373/413); button stays pinned low.
  padding-top: 84px;
  gap: 0;

  .game__points {
    margin-top: 48px;
  }
  .game__stars {
    margin-top: 22px;
  }
  .game__progress {
    margin-top: 8px;
  }
  .game__title {
    margin-top: 40px;
  }
  .game__copy {
    margin-top: 12px;
  }
}
.game__points {
  font-size: var(--fs-lg); // "Punten" label (Figma 393:428)
  color: var(--c-on-brand-soft);

  strong {
    margin-right: 4px;
    font-size: 32px; // big score number
    font-weight: var(--fw-bold);
    color: var(--c-white);
  }
}
.game__title {
  font-size: var(--fs-2xl); // Figma: 23px
}
.game__copy {
  max-width: 305px;
  font-size: var(--fs-md); // Figma: 15px
  line-height: 1.45;
  color: var(--c-on-brand-soft);
}

.game__stars {
  display: flex;
  gap: var(--sp-3);
  font-size: 34px;
}
.game__star {
  color: rgba(255, 255, 255, 0.4);
  &.is-on {
    color: var(--c-white); // Figma 393:428 — earned stars are white, not gold
  }
}
.game__progress {
  width: 252px;
  height: 16px;
  padding: 3px;
  background: var(--c-white);
  border-radius: 10px;
}
.game__progress-fill {
  display: block;
  height: 100%;
  border-radius: 7px;
  background: linear-gradient(90deg, #e60000, #f98519);
  transition: width var(--dur-slow) var(--ease-out);
}

.game__legend {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  text-align: left;

  li {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
  }
}
.game__legend-swatch {
  width: 22px;
  height: 22px;
  border-radius: 5px;
  &.is-correct {
    background: var(--c-tile-correct);
  }
  &.is-present {
    background: var(--c-tile-present);
  }
  &.is-absent {
    background: var(--c-tile-absent);
  }
}

.game__tip {
  width: 339px;
  max-width: 100%;
  padding: 14px 18px;
  font-size: var(--fs-md);
  line-height: 1.35;
  text-align: left;
  color: var(--c-on-brand);
  // Figma 393:291: 1px white border at 34% opacity, no fill
  border: 1px solid rgba(255, 255, 255, 0.34);
  border-radius: 14px;
}

// Fallback screens (unavailable / load error) reuse the game body rhythm.
.game__fallback {
  justify-content: center;
  padding-top: 0;
}
.game__fallback-emoji {
  font-size: 72px;
  line-height: 1;
}
.game__fallback-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-3);
}
.game__textlink {
  background: none;
  border: none;
  color: var(--c-on-brand-soft);
  font-size: var(--fs-md);
  text-decoration: underline;
}

.game__overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-5);
  background: var(--c-scrim);
  backdrop-filter: blur(4px);
}
.game__modal {
  width: 100%;
  max-width: 328px;
  padding: 25px;
  text-align: center;
  color: var(--c-ink);
  background: var(--c-white);
  border-radius: 28px;

  p {
    margin-top: var(--sp-2);
    color: var(--c-muted);
  }
}
.game__modal-actions {
  display: flex;
  gap: 10px;
  margin-top: var(--sp-5);
}
.modal-btn {
  flex: 1 1 0;
  height: 56px;
  font-family: var(--font-brand);
  font-size: var(--fs-lg);
  font-weight: var(--fw-bold);
  border-radius: 18px;

  &--ghost {
    color: var(--c-black);
    background: none;
    border: 1px solid var(--c-black);
  }
  &--primary {
    color: var(--c-white);
    background: var(--c-blue-deep);
    border: none;
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity var(--dur-fast);
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// The game's uitleg screen rises up from the bottom on entry — the same slide-up
// that used to be the home "we beginnen zo…" reveal card (now removed). `appear`
// runs it on the initial mount; phase changes (uitleg → play → score) reuse the
// same component instance, so it animates exactly once, on entry.
// Slow and heavily eased on purpose: the game sailing up over the reel is the
// pay-off of the spin, so it glides in (easeOutExpo) instead of snapping.
// The uitleg slides up from the bottom on entry. The game wrapper already carries
// `transform: translateY(0) scale(--pg-scale)` for the fit (games/gameHost.css),
// so the from-state repeats the scale: same two functions in the same order, which
// is what lets the browser interpolate them component-wise.
.rise-enter-active {
  transition: transform 0.95s cubic-bezier(0.16, 1, 0.3, 1);
}
.rise-enter-from {
  transform: translateY(100%) scale(var(--pg-scale, 1));
}
</style>
